<template>
  <div class="otb-filters-menu__container">
    <div class="otb-filters-menu__content">
      <div class="otb-filters-menu__grid-container wb-grid-container">
        <div class="otb-filters-menu__header-row">
          <div class="otb-filters-menu__header-col">
            <div class="otb-filters-menu__header">
              <button
                type="button"
                aria-label="Close Button"
                class="wb-round-button wb-round-button--small otb-filters-menu__close-btn"
                :disabled="!bookingSearchFinished"
                @click="dismissFiltersMenu"
              >
                <wb-icon
                  class="otb-filter-menu__close-btn-icon wb-round-button__icon"
                  aria-hidden="true"
                  name="close"
                ></wb-icon>
                {{ $t('schedule.filtersMenu.close') }}
              </button>
            </div>
          </div>
        </div>
        <div class="otb-filters-menu__body-row wb-grid-row">
          <div class="otb-filters-menu__body-col wb-grid-col-mq1-12">
            <div class="otb-filters-menu__body" :style="filtersMenuPaddingBottom">
              <section class="otb-filters-menu__mobile-search-section" v-if="showMobileSearchOptions">
                <h2 class="otb-filters-menu__mobile-search-header wb-type-heading-s">
                  <wb-icon
                    class="otb-filters-menu__mobile-search-header-icon"
                    aria-hidden="true"
                    name="bds/filter/24"
                  ></wb-icon>
                  {{ $t('schedule.filtersMenu.mobile.title') }}
                </h2>
                <div class="otb-filters-menu__mobile-location">
                  <template v-if="!hasFixedDealer">
                    <h3 class="wb-type-copy-strong">
                      <wb-icon class="otb-filters-menu__mobile-location-icon" name="bds/location/16"></wb-icon>
                      {{ $t('schedule.filtersMenu.mobile.location.title') }}
                    </h3>
                    <location-box class="otb-location-box" :validation="locationInputRoute" :hideTitle="true" />
                  </template>
                  <div class="otb-filters-menu__mobile-datetime-box">
                    <h3
                      class="otb-filters-menu__mobile-datetime-header wb-type-copy-strong"
                      :class="{ 'otb-filters-menu__mobile-datetime-header--small-margin': hasFixedDealer }"
                    >
                      <wb-icon class="otb-filters-menu__mobile-datetime-icon" name="time-xs"></wb-icon>
                      {{ $t('schedule.filtersMenu.mobile.date.title') }}
                    </h3>
                    <datetime-picker
                      :activeTextCarouselSlide="activeCalendarSlide"
                      :calendarType="calendarType"
                      :dealerCalendar="dealerCalendar"
                      :labelPlaceholder="$t('schedule.filtersMenu.mobile.date.input.label')"
                      :locale="localeCode"
                      :isMobile="isMobile"
                      :isOptional="true"
                      :selectedDates="selectedDates"
                      @changeDate="changeDate"
                      @changeSheet="changeSheet"
                    />
                  </div>
                </div>
              </section>
              <div class="otb-filters-menu__section-header">
                <h2 class="wb-type-copy-strong otb-filters-menu__section-title">
                  <wb-icon
                    class="otb-filters-menu__vehicle-options-icon otb-filters-menu__section-title__icon--vehicle"
                    aria-hidden="true"
                    :name="filtersMenuHeaderIcon"
                  ></wb-icon>
                  <span class="otb-filters-menu__section-title-label">{{
                    $t('schedule.filtersMenu.vehicleOptions')
                  }}</span>
                </h2>
                <div class="otb-filters-menu__section__button">
                  <button
                    type="button"
                    @click="vehicleFilterResetMethods()"
                    :disabled="enableClearVehiclePreferencesBtn"
                    class="wb-button wb-button--tertiary"
                  >
                    {{ $t('schedule.filtersMenu.clear') }}
                  </button>
                </div>
              </div>
              <div class="otb-filters-menu__section-body">
                <template v-if="showMobileDesign">
                  <filter-accordion
                    :icon="filterIconName('bds/fuel/24')"
                    :label="fuelTypeFilterMobileLabel"
                    :class="disabledClickClass(availableFuelOptions)"
                    :options="availableFuelOptions"
                    :selectedState="isFuelTypeSelected"
                    :selectedValue="selectedFiltersTags.fuelType"
                    :title="$t('schedule.filtersMenu.mobile.accordion.fuelType.title')"
                    @change="selectOption"
                    @close="dismissFiltersMenu"
                  />
                  <filter-accordion
                    :icon="filterIconName('bds/transmission/24')"
                    :label="transmissionFilterMobileLabel"
                    ::class="disabledClickClass(availableTransmissionOptions)"
                    :options="availableTransmissionOptions"
                    :selectedState="isTransmissionSelected"
                    :selectedValue="selectedFiltersTags.transmission"
                    :title="$t('schedule.filtersMenu.mobile.accordion.transmission.title')"
                    @change="selectOption"
                    @close="dismissFiltersMenu"
                  />
                  <filter-accordion
                    :icon="filterIconName('bds/engine/24')"
                    :selectedState="!!selectedMotorisation"
                    :label="motorisationFilterMobileLabel"
                    @close="dismissFiltersMenu"
                  >
                    <div class="otb-filter-accordion__title">
                      <wb-icon
                        v-if="!isVansContext"
                        class="otb-filter-accordion__title-icon"
                        name="bds/engine/24"
                      ></wb-icon>
                      <span class="wb-type-copy-strong">{{
                        $t('schedule.filtersMenu.mobile.accordion.engine.title')
                      }}</span>
                    </div>
                    <wb-card
                      v-for="(motorisation, index) in motorisationFilters"
                      class="otb-filter-accordion__card"
                      :class="checkedMotorisationClass(motorisation.value)"
                      :key="motorisation.value"
                    >
                      <label
                        class="wb-type-button-secondary otb-filter-accordion__option-label"
                        v-html="motorisation.text"
                        :for="`motorisation-${index}`"
                      ></label>
                      <div>
                        <wb-radio-control>
                          <label class="otb-filter-accordion__input">
                            <input
                              tabindex="-1"
                              type="radio"
                              name="motorisation"
                              :checked="isMotorisationChecked(motorisation.value)"
                              :id="`motorisation-${index}`"
                              :value="motorisation.value"
                              @change="motorisationChange($event)"
                            />
                          </label>
                        </wb-radio-control>
                      </div>
                    </wb-card>
                  </filter-accordion>
                  <div v-if="isVansContext">
                    <filter-accordion
                      :label="driveTypeFilterMobileLabel"
                      :options="availableDriveTypeOptions"
                      :class="disabledClickClass(availableDriveTypeOptions)"
                      :selectedState="isDriveTypeSelected"
                      :selectedValue="selectedFiltersTags.driveType"
                      :title="$t('schedule.filtersMenu.mobile.accordion.driveType.title')"
                      @change="selectOption"
                      @close="dismissFiltersMenu"
                    />
                    <filter-accordion
                      :label="wheelBaseFilterMobileLabel"
                      :options="availableWheelBaseOptions"
                      :class="disabledClickClass(availableWheelBaseOptions)"
                      :selectedState="isWheelbaseSelected"
                      :selectedValue="selectedFiltersTags.wheelBase"
                      :title="$t('schedule.filtersMenu.mobile.accordion.wheelBase.title')"
                      @change="selectOption"
                      @close="dismissFiltersMenu"
                    />
                    <filter-accordion
                      :label="weightFilterMobileLabel"
                      :options="availableWeightOptions"
                      :class="disabledClickClass(availableWeightOptions)"
                      :selectedState="isWeightSelected"
                      :selectedValue="selectedFiltersTags.weight"
                      :title="$t('schedule.filtersMenu.mobile.accordion.weight.title')"
                      :notification="weightNotificationInfo"
                      @change="selectOption"
                      @close="dismissFiltersMenu"
                    />
                  </div>
                </template>
                <template v-else>
                  <interactive-tag-group
                    class="otb-filters-menu__group"
                    :icon="filterGroupIcon('bds/fuel/24')"
                    :label="$t('shared.fuel.label')"
                    :options="availableFuelOptions"
                    :selectedValue="selectedFiltersTags.fuelType"
                    @change="selectOption"
                  />
                  <interactive-tag-group
                    class="otb-filters-menu__group"
                    :icon="filterGroupIcon('bds/transmission/24')"
                    :label="$t('shared.transmission.label')"
                    :options="availableTransmissionOptions"
                    :selectedValue="selectedFiltersTags.transmission"
                    @change="selectOption"
                  >
                  </interactive-tag-group>
                  <fieldset>
                    <h3 class="otb-filters-menu__group-title wb-type-label">
                      <wb-icon name="bds/engine/24" v-if="!isVansContext"></wb-icon
                      ><span class="otb-filters-menu__group-text">{{ $t('shared.engine.label') }}</span>
                    </h3>

                    <wb-select-control>
                      <wb-select>
                        <label class="otb-filters-menu__dropdown-label">{{ $t('shared.engine.label') }}</label>
                        <select
                          ref="motorisationSelection"
                          class="otb-filters-menu__dropdown"
                          :value="selectedMotorisationInputValue"
                          @change="motorisationChange($event)"
                        >
                          <option
                            v-for="(motorisation, index) in motorisationFilters"
                            :key="`motorisation-${index}`"
                            :value="motorisation.value"
                          >
                            {{ motorisation.text }}
                          </option>
                        </select>
                      </wb-select>
                    </wb-select-control>
                  </fieldset>
                  <div class="otb-filters-menu__vans__options" v-if="isVansContext">
                    <interactive-tag-group
                      class="otb-filters-menu__group"
                      :label="$t('shared.driveType.label')"
                      :options="availableDriveTypeOptions"
                      :selectedValue="selectedFiltersTags.driveType"
                      @change="selectOption"
                    />
                    <interactive-tag-group
                      class="otb-filters-menu__group"
                      :label="$t('shared.wheelBase.label')"
                      :options="availableWheelBaseOptions"
                      :selectedValue="selectedFiltersTags.wheelBase"
                      @change="selectOption"
                    />
                    <interactive-tag-group
                      class="otb-filters-menu__group"
                      :label="$t('shared.weight.label')"
                      :options="availableWeightOptions"
                      :selectedValue="selectedFiltersTags.weight"
                      @change="selectOption"
                    />
                  </div>
                  <div v-if="showWeightNotification" class="otb-filters-menu__weight__notification">
                    <wb-icon name="bds/info-circle/24"></wb-icon>
                    <p class="otb-filters-menu__weight__notification__text wb-type-copy-secondary">
                      {{ $t('schedule.booking.vehicleTile.weight.tooltip') }}
                    </p>
                  </div>
                </template>
              </div>
              <template v-if="false">
                <div class="otb-filters-menu__section-header">
                  <h2 class="wb-type-copy-strong otb-filters-menu__section-title">
                    <wb-icon class="otb-filters-menu__schedule-options-icon" aria-hidden="true" name="time"></wb-icon>
                    <span class="otb-filters-menu__section-title-label">{{ timeOptionsFilterTitleLabel }}</span>
                  </h2>
                  <div>
                    <button
                      type="button"
                      @click="periodFilterResetMethods"
                      :disabled="enableClearTimePeriodPreferencesBtn"
                      class="wb-button wb-button--tertiary"
                    >
                      {{ $t('schedule.filtersMenu.clear') }}
                    </button>
                  </div>
                </div>
                <div class="otb-filters-menu__section-body">
                  <template v-if="showMobileDesign">
                    <filter-accordion
                      icon="time"
                      :label="timeOptionsFilterMobileLabel"
                      :hideToggleIcon="true"
                      :options="availableTimePeriodOptions"
                      :selectedState="isTimePeriodSelected"
                      :selectedValue="selectedFiltersTags.timePeriod"
                      :title="$t('schedule.filtersMenu.mobile.accordion.dayPeriod.title')"
                      @change="selectOption"
                      @close="dismissFiltersMenu"
                    />
                  </template>
                  <template v-else>
                    <interactive-tag-group
                      class="otb-filters-menu__group"
                      :options="availableTimePeriodOptions"
                      :selectedValue="selectedFiltersTags.timePeriod"
                      @change="selectOption"
                    />
                  </template>
                </div>
              </template>
              <template v-if="!hasFixedDealer">
                <div class="otb-filters-menu__section-header otb-filters-menu__section-header--retailers">
                  <h2 class="wb-type-copy-strong otb-filters-menu__section-title">
                    <wb-icon
                      class="otb-filters-menu__section-title__icon--retailer"
                      aria-hidden="true"
                      name="bds/location-dealer/24"
                    ></wb-icon
                    ><span class="otb-filters-menu__section-title-label">{{
                      $t('schedule.filtersMenu.retailersOptions')
                    }}</span>
                  </h2>
                  <div class="otb-filters-menu__section__button">
                    <button
                      type="button"
                      @click="dealerFilterResetMethods()"
                      :disabled="!enableClearDealersPreferencesBtn"
                      class="wb-button wb-button--tertiary"
                    >
                      {{ $t('schedule.filtersMenu.clear') }}
                    </button>
                  </div>
                </div>

                <div class="otb-filters-menu__dealer-search__wrapper">
                  <p v-if="!showMobileDesign" class="wb-type-copy-secondary otb-filters-menu__dealer-search__location">
                    {{ selectedLocation }}
                  </p>
                  <wb-input-control class="otb-filters-menu__dealer-search" v-if="showDesktopSearchBar">
                    <wb-input @input="getTextSearchResults()">
                      <label>{{ $t('schedule.filtersMenu.retailersSearch.placeholder') }}</label>
                      <input type="text" v-model="textSearch" />
                      <wb-icon
                        class="otb-filters-menu__dealer-search-icon wb-round-button__icon"
                        name="bds/search/24"
                      />
                    </wb-input>
                  </wb-input-control>
                </div>

                <wb-notification
                  v-if="noDealersFound && !showMobileDesign"
                  icon="bds/info-circle/24"
                  theme="dark"
                  class="wb-margin-bottom-s schedule__no-results-notification"
                  hide-dismiss-button
                >
                  <wb-notification-content>
                    {{ $t('schedule.noDealers.notification') }}
                  </wb-notification-content>
                </wb-notification>
                <div class="otb-filters-menu__dealer-list" :class="dealersListClasses" v-if="!showMobileDesign">
                  <wb-checkbox-control
                    v-for="(dealer, index) in filteredDealers"
                    :key="`dealer-${index}`"
                    class="otb-filters-menu__dealer-checkbox"
                    @change="dealerChange($event)"
                  >
                    <label class="otb-filters-menu__dealer-label">
                      <input
                        type="checkbox"
                        :disabled="dealer.disabled"
                        :checked="currentlySelectedDealers.includes(dealer.value)"
                        :value="dealer.value"
                      />
                      <div class="otb-filters-menu__dealer__text">
                        <span class="otb-filters-menu__dealer-name wb-type-button">{{ dealer.text }}</span>
                        <span
                          class="otb-filters-menu__dealer-name otb-filters-menu__dealer-name--secondary wb-type-button-secondary"
                          >{{ dealer.address }}</span
                        >
                      </div>
                      <!-- TODO Code responsible for displaying the distance of a dealer
                        Uncomment in the future when dealers are sorted by distance -->
                      <!-- <span class="otb-filters-menu__dealer__distance">
                        <wb-icon name="bds/location/16" class="otb-filters-menu__dealer__distance__icon"></wb-icon>
                        <span class="wb-type-copy-tertiary otb-filters-menu__dealer__distance__text"
                          >{{ getDealerDistance(dealer.distanceValue, dealer.distanceUnit) }}
                        </span>
                      </span> -->
                    </label>
                  </wb-checkbox-control>
                </div>
              </template>

              <div class="otb-filters-menu__section-body" v-if="showDealersInMobile">
                <filter-accordion
                  :label="dealersFilterMobileLabel"
                  :selectedCount="currentlySelectedDealers.length"
                  :selectedValue="selectedFiltersTags.transmission"
                  @close="dismissFiltersMenu"
                >
                  <div class="otb-filter-accordion__title">
                    <wb-icon class="otb-filter-accordion__title-icon" name="bds/location-dealer/24"></wb-icon>
                    <span class="wb-type-copy-strong">{{
                      $t('schedule.filtersMenu.mobile.accordion.retailers.title')
                    }}</span>
                  </div>
                  <div v-if="noDealersFound">
                    <p
                      class="wb-type-copy-secondary otb-filters-menu__dealer-search__location otb-filters-menu__dealer-search__location--mobile"
                    >
                      {{ selectedLocation }}
                    </p>
                    <span class="otb-filters-menu__dealer-search__divider"></span>
                    <wb-notification
                      icon="bds/info-circle/24"
                      theme="dark"
                      class="wb-margin-bottom-s schedule__no-results-notification"
                      hide-dismiss-button
                    >
                      <wb-notification-content>
                        {{ $t('schedule.noDealers.notification') }}
                      </wb-notification-content>
                    </wb-notification>
                  </div>

                  <div class="otb-filter-accordion__section-wrapper" v-if="showMobileSearchBar">
                    <wb-input-control class="otb-filters-menu__dealer-search">
                      <p
                        class="wb-type-copy-secondary otb-filters-menu__dealer-search__location otb-filters-menu__dealer-search__location--mobile"
                      >
                        {{ selectedLocation }}
                      </p>
                      <wb-input @input="getTextSearchResults()">
                        <label>{{ $t('schedule.filtersMenu.retailersSearch.placeholder') }}</label>
                        <input type="text" v-model="textSearch" />
                        <wb-icon
                          class="otb-filters-menu__dealer-search-icon wb-round-button__icon"
                          name="bds/search/24"
                        ></wb-icon>
                      </wb-input>
                    </wb-input-control>
                  </div>
                  <div v-if="bookingSearchFinished">
                    <wb-card
                      v-for="(dealer, index) in filteredDealers"
                      class="otb-filter-accordion__card otb-filter-accordion__card--dealer"
                      :class="checkedDealerClass(dealer.text)"
                      :key="`dealer-${index}`"
                    >
                      <div class="otb-filter-accordion__dealer">
                        <span class="otb-filter-accordion__dealer__text">
                          <label
                            class="wb-type-button-secondary otb-filter-accordion__option-label"
                            :for="`dealer-${index}`"
                            v-html="dealer.text"
                          ></label>
                          <label
                            class="wb-type-button-secondary otb-filter-accordion__option-label otb-filter-accordion__dealer__text--secondary"
                            :for="`dealer-${index}`"
                          >
                            {{ dealer.address }}
                          </label>
                        </span>
                        <!-- TODO Code responsible for displaying the distance of a dealer
                          Uncomment in the future when dealers are sorted by distance -->
                        <!-- <span class="otb-filter-accordion__dealer__distance">
                            <wb-icon
                              name="bds/location/16"
                              class="otb-filter-accordion__dealer__distance__icon"
                            ></wb-icon>
                            <span class="wb-type-copy-tertiary otb-filter-accordion__dealer__distance__text"
                              >{{ getDealerDistance(dealer.distanceValue, dealer.distanceUnit) }}
                            </span>
                          </span> -->
                      </div>

                      <wb-checkbox-control @change="dealerChange($event)">
                        <label class="otb-filter-accordion__input otb-filter-accordion__input--dealer">
                          <input
                            type="checkbox"
                            :checked="currentlySelectedDealers.includes(dealer.value)"
                            :disabled="dealer.disabled"
                            :id="`dealer-${index}`"
                            :value="dealer.value"
                          />
                        </label>
                      </wb-checkbox-control>
                    </wb-card>
                  </div>
                  <div class="otb-filters-menu__dealer-spinner-wrapper" v-else>
                    <wb-spinner class="otb-filters-menu__dealer-spinner" theme="light"></wb-spinner>
                  </div>
                </filter-accordion>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="otb-filters-menu__footer" ref="footerRef">
      <button
        type="button"
        @click="resetAllFiltersAndAvailableMotorisations"
        class="wb-button wb-button--tertiary wb-button--medium otb-filters-menu__reset-btn"
        v-if="showMobileDesign"
      >
        {{ $t('shared.form.resetFilters') }}
      </button>
      <button
        type="button"
        @click="dismissFiltersMenu"
        :disabled="!bookingSearchFinished"
        class="wb-button wb-button--primary wb-button--medium otb-filters-menu__apply-btn"
      >
        <wb-icon v-if="!showMobileDesign" class="wb-button__icon" aria-hidden="true" name="bds/filter/24"></wb-icon>
        {{ $t('schedule.filtersMenu.seeResults') }}
        <wb-counter
          class="otb-filters-menu__apply-counter"
          :value="numberOfFilteredBookingResults"
          theme="dark"
          mode="secondary"
          v-if="bookingSearchFinished"
        ></wb-counter>
        <wb-spinner v-else theme="blue" class="otb-filters-menu__apply-spinner"></wb-spinner>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { i18n } from '@dh-io-otb/shared/src/services/translation.service';
import { useFilters } from '../../composables/filters.composable';
import { useRoutingApplication } from '../../composables/routing-application.composable';
import { CONTEXT_OPTIONS, ROUTER, WEIGHT_CATEGORY } from '../../models/constants';
import { useStore } from '@dh-io-otb/shared/src/composables/store.composable';
import { useMediaQuery } from '@dh-io-otb/shared/src/composables/media-query.composable';
import { useUtils } from '@dh-io-otb/shared/src/composables/utils.composable';
import CalendarService from '@dh-io-otb/shared/src/services/calendar.service';
import UtilsService from '@dh-io-otb/shared/src/services/utils.service';
import { SlideInformation } from '@dh-io-otb/shared/src/models/entities/text-carousel.model';
import { DateRangeMode } from '@dh-io-otb/shared/src/models/entities/calendar.model';
import {
  DatetimePicker,
  FilterAccordion,
  InteractiveTagGroup,
  LocationBox,
} from '@dh-io-otb/shared/src/components/index';
import {
  FiltersMenuDealersListClasses,
  FilterGroupType,
  FiltersDefaultValue,
  FilterOption,
} from '../../models/entities/filters.model';
import { TRACKING_EVENT_ACTION, TRACKING_EVENT_NAME } from '@dh-io-otb/shared/src/models/constants';

export default defineComponent({
  name: 'otb-filters-menu',
  components: {
    DatetimePicker,
    FilterAccordion,
    InteractiveTagGroup,
    LocationBox,
  },
  props: {
    selectedDates: Object as () => DateRangeMode,
  },
  setup(_, { emit }) {
    const { path } = useRoutingApplication();
    const {
      timePeriodFilters,
      fuelTypeFilters,
      transmissionFilters,
      motorisationFilters,
      allVehicleFilters,
      resetFilters,
      resetAllFiltersAndAvailableMotorisations,
      numberOfFilteredBookingResults,
      motorisationChange,
      dealerChange,
      selectOption,
      dealersFilters,
      enableClearVehiclePreferencesBtn,
      enableClearTimePeriodPreferencesBtn,
      enableClearDealersPreferencesBtn,
      trackClearFilterEvents,
      initializeFilters,
    } = useFilters();

    const footerRef = ref(null);
    const footerHeight = ref(0);
    const { commit, getter, state } = useStore();
    const { isMobile, isMq3, isMq4 } = useMediaQuery();
    const { calendarType } = useUtils();
    const activeCalendarSlide = ref(0);
    const dealerCalendar = ref({ availableDates: [''], closedDates: [''] });
    const fuelTypeSelectedValue = ref('');
    const transmissionSelectedValue = ref('');
    const timePeriodSelectedValue = ref('');
    const driveTypeSelectedValue = ref('');
    const wheelBaseSelectedValue = ref('');
    const weightSelectedValue = ref('');
    const motorisationSelection = ref();
    const textSearch = ref('');
    let filteredDealers = ref();
    // lifecycle
    onMounted((): void => {
      initializeFilters();
      filteredDealers.value = state.otbcFlow.availableDealersOptions;
      dealerCalendar.value = CalendarService.buildGenericCalendar(getter.initialUnavailableDays);
    });
    const localeCode = computed((): string => getter.localeCode);

    const showMobileDesign = computed((): boolean => isMobile.value || isMq3.value);

    const showMobileSearchOptions = computed((): boolean => showMobileDesign.value || isMq4.value);

    const changeDate = (day: Date): void => {
      commit('selectedDealerIds', []);
      emit('changeSelectedDate', day);
      commit('activeCalendarSlide', activeCalendarSlide.value);
    };

    const changeSheet = (page: SlideInformation): void => {
      activeCalendarSlide.value = page.index;
    };
    const filtersApplied = computed((): any => {
      const preferencesObject = {
        fuelType: state.otbcFlow.filtersApplied.fuelType,
        transmission: state.otbcFlow.filtersApplied.transmission,
        timePeriod: state.otbcFlow.filtersApplied.timePeriod,
        driveType: state.otbcFlow.filtersApplied.driveType,
        wheelBase: state.otbcFlow.filtersApplied.wheelBase,
        weight: state.otbcFlow.filtersApplied.weight,
      };
      return preferencesObject;
    });
    const filterDealerList = (availableDealers: FilterOption[], filterText: string) => {
      const filteredDealerObjects = availableDealers.filter((object: FilterOption) =>
        object.text ? (object.text as string).toLowerCase().includes(filterText.toLowerCase()) : true,
      );
      filteredDealers.value = filteredDealerObjects;
      return filteredDealerObjects;
    };

    const locationInputRoute = computed((): boolean => {
      return path?.value === ROUTER.SCHEDULE;
    });

    const selectedLocation = computed((): any => {
      return `${state.otbcFlow.initialLocation?.label} (${state.otbcFlow.locationRadius})`;
    });
    const noDealersFound = computed((): any => {
      return state.otbcFlow.dealersSearchResults.length === 0;
    });
    // For countries with characters that do not use the latin alphabet (ex.: JP, KR, TH...)
    // we need to make the search after 1 character is input.
    const nonLatinCharacters = /[^\u0020-\u007f]/;
    const getTextSearchResults = (): void => {
      if (textSearch.value && (nonLatinCharacters.test(textSearch.value) || textSearch.value.length >= 2)) {
        filterDealerList(availableDealersOptions.value, textSearch.value);
      } else {
        filteredDealers.value = state.otbcFlow.availableDealersOptions;
      }
      commit('dealerTextFilterSearch', textSearch.value);
    };
    const clearDealerTextBox = (): void => {
      textSearch.value = '';
      getTextSearchResults();
    };
    const filterGroupIcon = (icon: string): string => {
      return isVansContext.value ? '' : icon;
    };
    const weightNotificationInfo = computed((): any => {
      return {
        icon: 'bds/info-circle/16',
        text: i18n.t('schedule.booking.vehicleTile.weight.tooltip'),
        value: 'HEAVY_WEIGHT',
      };
    });

    const filtersMenuPaddingBottom = computed((): any => {
      return { paddingBottom: footerHeight.value + 'px' };
    });

    const isVansContext = computed((): boolean => {
      return state.configuration.context === CONTEXT_OPTIONS.VANS;
    });
    const filtersMenuHeaderIcon = computed((): string => {
      return isVansContext.value ? 'bds/van-frontal/24' : 'bds/car/24';
    });
    const currentlySelectedDealers = computed((): string[] => {
      return state.otbcFlow.selectedDealerIds;
    });
    const isDealersListLarge = computed((): boolean => {
      return availableDealersOptions.value.length >= 5;
    });
    const filteredBookingsSearchResults = computed((): boolean => {
      return state.otbcFlow.filteredBookingsSearchResults.length;
    });
    const availableTransmissionOptions = computed((): any[] => {
      return state.otbcFlow.availableTransmissionOptions;
    });
    const availableDealersOptions = computed((): any[] => {
      return state.otbcFlow.availableDealersOptions;
    });
    const availableFuelOptions = computed((): any[] => {
      return state.otbcFlow.availableFuelOptions;
    });
    const availableTimePeriodOptions = computed((): any[] => {
      return state.otbcFlow.availableTimePeriodOptions;
    });
    const availableDriveTypeOptions = computed((): any[] => {
      return state.otbcFlow.availableDriveTypeOptions;
    });
    const availableWheelBaseOptions = computed((): any[] => {
      return state.otbcFlow.availableWheelBaseOptions;
    });
    const availableWeightOptions = computed((): any[] => {
      return state.otbcFlow.availableWeightOptions;
    });
    const selectedMotorisation = computed((): any => {
      return state.otbcFlow.selectedMotorisation;
    });
    const selectedMotorisationInputValue = computed((): any => {
      return state.otbcFlow.selectedMotorisation && state.otbcFlow.selectedMotorisation.id
        ? state.otbcFlow.selectedMotorisation.id
        : FiltersDefaultValue.ALLMOTORISATION;
    });
    const bookingSearchFinished = computed((): boolean => {
      return state.otbcFlow.bookingSearchFinished;
    });
    const hasFixedDealer = computed((): boolean => {
      return state.otbcFlow.fixedDealer;
    });
    const showDealersInMobile = computed((): boolean => {
      return !hasFixedDealer.value && showMobileDesign.value;
    });
    const filterIconName = (iconName: string): string => {
      return !isVansContext.value ? iconName : '';
    };
    const dealersListClasses = computed((): FiltersMenuDealersListClasses => {
      return {
        'otb-filters-menu__dealer--large': isDealersListLarge.value,
      };
    });
    const clearMotorisationDropdown = (): void => {
      const selectElement = motorisationSelection.value;
      const optionElement = selectElement?.querySelector('option[value="ALLMOTORISATION"]');
      if (optionElement) {
        optionElement.selected = true;
      }
    };
    const dismissFiltersMenu = (): void => {
      emit('dismiss');
      UtilsService.closeAllAccordions();
    };
    const selectedFiltersTags = computed((): any => {
      return {
        fuelType: fuelTypeInputSelected(),
        transmission: transmissionInputSelected(),
        timePeriod: timePeriodInputSelected(),
        driveType: driveTypeInputSelected(),
        wheelBase: wheelBaseInputSelected(),
        weight: weightInputSelected(),
      };
    });
    const driveTypeInputSelected = (): string => {
      if (state.otbcFlow.availableDriveTypeOptions && state.otbcFlow.availableDriveTypeOptions.length === 1) {
        driveTypeSelectedValue.value = state.otbcFlow.availableDriveTypeOptions[0].value;
        return driveTypeSelectedValue.value;
      }
      driveTypeSelectedValue.value = !filtersApplied.value.driveType
        ? FiltersDefaultValue.ALLDRIVETYPE
        : filtersApplied.value.driveType;
      return driveTypeSelectedValue.value;
    };
    const wheelBaseInputSelected = (): string => {
      if (state.otbcFlow.availableWheelBaseOptions && state.otbcFlow.availableWheelBaseOptions.length === 1) {
        wheelBaseSelectedValue.value = state.otbcFlow.availableWheelBaseOptions[0].value;
        return wheelBaseSelectedValue.value;
      }
      wheelBaseSelectedValue.value = !filtersApplied.value.wheelBase
        ? FiltersDefaultValue.ALLWHEELBASE
        : filtersApplied.value.wheelBase;
      return wheelBaseSelectedValue.value;
    };
    const weightInputSelected = (): string => {
      if (state.otbcFlow.availableWeightOptions && state.otbcFlow.availableWeightOptions.length === 1) {
        weightSelectedValue.value = state.otbcFlow.availableWeightOptions[0].value;
        return weightSelectedValue.value;
      }
      weightSelectedValue.value = !filtersApplied.value.weight
        ? FiltersDefaultValue.ALLWEIGHT
        : filtersApplied.value.weight;
      return weightSelectedValue.value;
    };
    const fuelTypeInputSelected = (): string => {
      if (state.otbcFlow.availableFuelOptions && state.otbcFlow.availableFuelOptions.length === 1) {
        fuelTypeSelectedValue.value = state.otbcFlow.availableFuelOptions[0].value;
        return fuelTypeSelectedValue.value;
      }
      fuelTypeSelectedValue.value = !filtersApplied.value.fuelType
        ? FiltersDefaultValue.ALLFUELTYPE
        : filtersApplied.value.fuelType;
      return fuelTypeSelectedValue.value;
    };
    const transmissionInputSelected = (): string => {
      if (state.otbcFlow.availableTransmissionOptions && state.otbcFlow.availableTransmissionOptions.length === 1) {
        transmissionSelectedValue.value = state.otbcFlow.availableTransmissionOptions[0].value;
        return transmissionSelectedValue.value;
      }
      transmissionSelectedValue.value = !filtersApplied.value.transmission
        ? FiltersDefaultValue.ALLTRANSMISSION
        : filtersApplied.value.transmission;
      return transmissionSelectedValue.value;
    };
    const timePeriodInputSelected = (): string => {
      timePeriodSelectedValue.value = !filtersApplied.value.timePeriod
        ? FiltersDefaultValue.ALLDAY
        : filtersApplied.value.timePeriod;
      return timePeriodSelectedValue.value;
    };
    const isMotorisationChecked = (motorisation: string): boolean => {
      if (
        (motorisation === FiltersDefaultValue.ALLMOTORISATION && !state.otbcFlow.selectedMotorisation) ||
        (state.otbcFlow.selectedMotorisation && state.otbcFlow.selectedMotorisation.id === motorisation)
      ) {
        return true;
      }
      return false;
    };
    const fuelTypeFilterMobileLabel = computed(() => {
      const fuelType = availableFuelOptions.value.find((option) => option.value === selectedFiltersTags.value.fuelType);
      return fuelType && fuelType.value !== FiltersDefaultValue.ALLFUELTYPE
        ? fuelType.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.fuelType.title');
    });
    const transmissionFilterMobileLabel = computed(() => {
      const transmission = availableTransmissionOptions.value.find(
        (option) => option.value === selectedFiltersTags.value.transmission,
      );
      return transmission && transmission.value !== FiltersDefaultValue.ALLTRANSMISSION
        ? transmission.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.transmission.title');
    });
    const driveTypeFilterMobileLabel = computed(() => {
      const driveType = availableDriveTypeOptions.value.find(
        (option) => option.value === selectedFiltersTags.value.driveType,
      );
      return driveType && driveType.value !== FiltersDefaultValue.ALLDRIVETYPE
        ? driveType.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.driveType.title');
    });
    const wheelBaseFilterMobileLabel = computed(() => {
      const wheelBase = availableWheelBaseOptions.value.find(
        (option) => option.value === selectedFiltersTags.value.wheelBase,
      );
      return wheelBase && wheelBase.value !== FiltersDefaultValue.ALLWHEELBASE
        ? wheelBase.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.wheelBase.title');
    });
    const weightFilterMobileLabel = computed(() => {
      const weight = availableWeightOptions.value.find((option) => option.value === selectedFiltersTags.value.weight);
      return weight && weight.value !== FiltersDefaultValue.ALLWEIGHT
        ? weight.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.weight.title');
    });
    const motorisationFilterMobileLabel = computed(() => {
      const motorisation = motorisationFilters.value.find((option) => {
        if (state.otbcFlow.selectedMotorisation && state.otbcFlow.selectedMotorisation.id) {
          return option.value === state.otbcFlow.selectedMotorisation.id;
        }
        return false;
      });
      return motorisation ? motorisation.text : i18n.t('schedule.filtersMenu.mobile.accordion.motorisation.title');
    });
    const timeOptionsFilterMobileLabel = computed(() => {
      const period = availableTimePeriodOptions.value.find(
        (option) => option.value === selectedFiltersTags.value.timePeriod,
      );
      return period && period.value !== FiltersDefaultValue.ALLDAY
        ? period.text
        : i18n.t('schedule.filtersMenu.mobile.accordion.dayPeriod.title');
    });
    const timeOptionsFilterTitleLabel = computed(() => {
      return isMobile.value
        ? i18n.t('schedule.filtersMenu.mobile.dayPeriod.title')
        : i18n.t('schedule.filtersMenu.dayPeriodOptions');
    });
    const dealersFilterMobileLabel = computed(() => {
      if (currentlySelectedDealers.value.length === 0) return i18n.t('schedule.filtersMenu.mobile.retailers.title');
      const dealersNames: string[] = [];
      currentlySelectedDealers.value.forEach((dealerId) => {
        const dealer = state.otbcFlow.dealersSearchResults.find((dealer) => dealer.id === dealerId);
        dealersNames.push(dealer.computedName);
      });
      return dealersNames.join(', ');
    });
    const showDesktopSearchBar = computed(() => {
      return isDealersListLarge.value && !showMobileDesign.value;
    });
    const showMobileSearchBar = computed(() => {
      return isDealersListLarge.value && bookingSearchFinished.value;
    });
    const checkedMotorisationClass = (motorisation: string): string =>
      isMotorisationChecked(motorisation) ? 'otb-filter-accordion__toggle-label--checked' : '';
    const checkedDealerClass = (dealer: string): string =>
      currentlySelectedDealers.value.includes(dealer) ? 'otb-filter-accordion__toggle-label--checked' : '';
    const isFuelTypeSelected = computed((): boolean => {
      return (
        (filtersApplied.value.fuelType !== FiltersDefaultValue.ALLFUELTYPE && !!filtersApplied.value.fuelType) ||
        availableFuelOptions.value.length === 1
      );
    });
    const isTransmissionSelected = computed((): boolean => {
      return (
        (filtersApplied.value.transmission !== FiltersDefaultValue.ALLTRANSMISSION &&
          !!filtersApplied.value.transmission) ||
        availableTransmissionOptions.value.length === 1
      );
    });
    const isTimePeriodSelected = computed((): boolean => {
      return (
        (filtersApplied.value.timePeriod !== FiltersDefaultValue.ALLDAY && !!filtersApplied.value.timePeriod) ||
        availableTimePeriodOptions.value.length === 1
      );
    });
    const isDriveTypeSelected = computed((): boolean => {
      return (
        (filtersApplied.value.driveType !== FiltersDefaultValue.ALLDRIVETYPE && !!filtersApplied.value.driveType) ||
        availableDriveTypeOptions.value.length === 1
      );
    });
    const isWheelBaseSelected = computed((): boolean => {
      return (
        (filtersApplied.value.wheelBase !== FiltersDefaultValue.ALLWHEELBASE && !!filtersApplied.value.wheelBase) ||
        availableWheelBaseOptions.value.length === 1
      );
    });
    const isWeightSelected = computed((): boolean => {
      return (
        (filtersApplied.value.weight !== FiltersDefaultValue.ALLWEIGHT && !!filtersApplied.value.weight) ||
        availableWeightOptions.value.length === 1
      );
    });

    const showWeightNotification = computed(() => {
      return (
        (state.otbcFlow.selectedWeight === WEIGHT_CATEGORY.HEAVY_WEIGHT ||
          state.otbcFlow.selectedMotorisation?.weightCategory === WEIGHT_CATEGORY.HEAVY_WEIGHT) &&
        !isMobile.value
      );
    });
    const dealerFilterResetMethods = (): void => {
      resetFilters(FilterGroupType.DEALERS);
      clearDealerTextBox();
      trackClearFilterEvents(TRACKING_EVENT_ACTION.RETAILER, TRACKING_EVENT_NAME.APPLY_FILTER);
    };
    const vehicleFilterResetMethods = (): void => {
      resetFilters(FilterGroupType.VEHICLE);
      clearMotorisationDropdown();
      trackClearFilterEvents(TRACKING_EVENT_ACTION.VEHICLE, TRACKING_EVENT_NAME.APPLY_FILTER);
    };

    const periodFilterResetMethods = (): void => {
      resetFilters(FilterGroupType.TIME_PERIOD);
      trackClearFilterEvents(TRACKING_EVENT_ACTION.PERIOD, TRACKING_EVENT_NAME.APPLY_FILTER);
    };

    const disabledClickClass = (filterOption): string => {
      return filterOption.length === 1 ? 'disabled-click' : '';
    };

    // TODO Code responsible for displaying the distance of a dealer
    // Uncomment in the future when dealers are sorted by distance
    // const getDealerDistance = (distanceValue, distanceUnit): string => {
    //   const formattedDistance = distanceValue?.replace('.', ',');
    //   return `${formattedDistance} ${distanceUnit}`;
    // };

    watch(
      () => state.otbcFlow.availableDealersOptions,
      () => {
        filteredDealers.value = state.otbcFlow.availableDealersOptions;
      },
    );

    watch(
      () => footerRef?.value?.offsetHeight,
      () => {
        footerHeight.value = footerRef?.value?.offsetHeight + 20;
      },
    );

    return {
      // METHODS
      changeDate,
      changeSheet,
      checkedDealerClass,
      checkedMotorisationClass,
      dismissFiltersMenu,
      fuelTypeInputSelected,
      isMotorisationChecked,
      transmissionInputSelected,
      timePeriodInputSelected,
      getTextSearchResults,
      clearDealerTextBox,
      dealerFilterResetMethods,
      vehicleFilterResetMethods,
      periodFilterResetMethods,
      filterGroupIcon,
      filterIconName,
      disabledClickClass,
      // TODO Code responsible for displaying the distance of a dealer
      // Uncomment in the future when dealers are sorted by distance
      // getDealerDistance,

      // FROM FILTERS COMPOSABLE
      timePeriodFilters,
      fuelTypeFilters,
      transmissionFilters,
      motorisationFilters,
      dealersFilters,
      allVehicleFilters,
      resetFilters,
      resetAllFiltersAndAvailableMotorisations,
      numberOfFilteredBookingResults,
      motorisationChange,
      dealerChange,
      selectOption,
      enableClearVehiclePreferencesBtn,
      enableClearTimePeriodPreferencesBtn,
      enableClearDealersPreferencesBtn,
      trackClearFilterEvents,

      // CONSTANTS & ENUMS
      FilterGroupType,
      FiltersDefaultValue,
      // COMPUTEDS
      availableTransmissionOptions,
      availableFuelOptions,
      availableDealersOptions,
      availableTimePeriodOptions,
      availableDriveTypeOptions,
      availableWheelBaseOptions,
      availableWeightOptions,
      bookingSearchFinished,
      calendarType,
      dealersFilterMobileLabel,
      filteredBookingsSearchResults,
      fuelTypeFilterMobileLabel,
      isDealersListLarge,
      noDealersFound,
      isFuelTypeSelected,
      isDriveTypeSelected,
      isWheelBaseSelected,
      isWeightSelected,
      isMobile,
      showMobileDesign,
      showMobileSearchOptions,
      showDealersInMobile,
      isTimePeriodSelected,
      isTransmissionSelected,
      localeCode,
      locationInputRoute,
      motorisationFilterMobileLabel,
      showDesktopSearchBar,
      showMobileSearchBar,
      timeOptionsFilterMobileLabel,
      timeOptionsFilterTitleLabel,
      transmissionFilterMobileLabel,
      driveTypeFilterMobileLabel,
      wheelBaseFilterMobileLabel,
      weightFilterMobileLabel,
      dealersListClasses,
      currentlySelectedDealers,
      filtersApplied,
      selectedFiltersTags,
      hasFixedDealer,
      selectedMotorisationInputValue,
      filtersMenuHeaderIcon,
      isVansContext,
      weightNotificationInfo,
      showWeightNotification,
      filtersMenuPaddingBottom,
      selectedLocation,
      // REFS
      activeCalendarSlide,
      dealerCalendar,
      textSearch,
      filteredDealers,
      clearMotorisationDropdown,
      motorisationSelection,
      selectedMotorisation,
      footerRef,
      footerHeight,
    };
  },
});
</script>

<style lang="scss" src="./filters-menu.scss" scoped></style>
